export const values = [
  {
    title: 'Calm',
    offset: 0,
    percentage: 100 / 7,
    segmentColor: '#bfe5e4',
    subEmotions: ['Serious', 'Bored', 'Quiet', 'Thankful', 'Peaceful', 'Content', 'Relaxed'],
    subEmotionColor: '#daeff1',
  },
  {
    title: 'Happy',
    offset: 100 / 7,
    percentage: 14.285714,
    segmentColor: '#fdf0aa',
    subEmotions: ['Loved', 'Excited/Energetic', 'Joyful', 'Optimistic/Hopeful', 'Grateful', 'Amused', 'Passionate'],
    subEmotionColor: '#fef8d8',
  },
  {
    title: 'Confident',
    offset: 100 / 7,
    percentage: 100 / 7,
    segmentColor: '#f58955',
    subEmotions: ['Brave/Courageous', 'Assertive', 'Powerful', 'Respected/Valued', 'Intelligent', 'Proud', 'Important'],
    subEmotionColor: '#f5c4b1',
  },
  {
    title: 'Surprised',
    offset: 0,
    percentage: 14.285714,
    segmentColor: '#f15e4a',
    subEmotions: ['Startled', 'Overwhelmed', 'Shocked', 'Confused', 'Stunned', 'Amazed', 'Fascinated'],
    subEmotionColor: '#f59192',
  },
  {
    title: 'Angry',
    offset: 0,
    percentage: 14.285714,
    segmentColor: '#181597',
    subEmotions: ['Hurt', 'Upset', 'Jealous', 'Frustrated', 'Annoyed', 'Offended', 'Irritated'],
    subEmotionColor: '#5982bc',
  },
  {
    title: 'Scared',
    offset: 0,
    percentage: 14.285714,
    segmentColor: '#5A81B9',
    subEmotions: ['Anxious', 'Intimidated', 'Embarrassed', 'Weak', 'Distracted/Forgetful', 'Stressed', 'Nervous'],
    subEmotionColor: '#91b6e0',
  },
  {
    title: 'Sad',
    offset: 0,
    percentage: 14.285714,
    segmentColor: '#6db8e3',
    subEmotions: [
      'Disappointed',
      'Insecure',
      'Guilty/Ashamed',
      'Depressed',
      'Lonely/Disconnected',
      'Discouraged',
      'Misunderstood',
    ],
    subEmotionColor: '#aee0f6',
  },
];

export const getInnerSlicePath = (
  centreX: number,
  centreY: number,
  startAngleRadians: number,
  sweepAngleRadians: number,
  radius: number,
) => {
  const firstCircumferenceX = centreX + radius * Math.cos(startAngleRadians);
  const firstCircumferenceY = centreY + radius * Math.sin(startAngleRadians);
  const secondCircumferenceX = centreX + radius * Math.cos(startAngleRadians + sweepAngleRadians);
  const secondCircumferenceY = centreY + radius * Math.sin(startAngleRadians + sweepAngleRadians);

  return `M${centreX} ${centreY} L${firstCircumferenceX} ${firstCircumferenceY} A${radius} ${radius} 0 0 1 ${secondCircumferenceX} ${secondCircumferenceY} Z`;
};

export const getInnerTextCoordinates = (
  centreX: number,
  centreY: number,
  startAngleRadians: number,
  sweepAngleRadians: number,
  radius: number,
): [number, number] => {
  const secondCircumferenceX = centreX + radius * Math.sin(startAngleRadians + sweepAngleRadians * 0.5);
  const secondCircumferenceY = centreY + radius * Math.cos(startAngleRadians + sweepAngleRadians * 0.5);

  return [secondCircumferenceX, secondCircumferenceY];
};

export const getOuterSlicePath = (
  centreX: number,
  centreY: number,
  startAngleRadians: number,
  sweepAngleRadians: number,
  radius: number,
) => {
  const startX = centreX + radius * Math.cos(startAngleRadians);
  const startY = centreY + radius * Math.sin(startAngleRadians);
  const firstArcEndX = centreX + radius * Math.cos(startAngleRadians + sweepAngleRadians);
  const firstArcEndY = centreY + radius * Math.sin(startAngleRadians + sweepAngleRadians);
  const lineEndX = firstArcEndX + radius * Math.cos(startAngleRadians + sweepAngleRadians);
  const lineEndY = firstArcEndY + radius * Math.sin(startAngleRadians + sweepAngleRadians);
  const secondArcEndX = startX + radius * Math.cos(startAngleRadians);
  const secondArcEndY = startY + radius * Math.sin(startAngleRadians);

  return `M${startX} ${startY} A${radius} ${radius} 0 0 1 ${firstArcEndX} ${firstArcEndY} L${lineEndX} ${lineEndY} A${
    2 * radius
  } ${2 * radius} 0 0 0 ${secondArcEndX} ${secondArcEndY} Z`;
};

export const getOuterTextCoordinates = (
  centreX: number,
  centreY: number,
  startAngleRadians: number,
  sweepAngleRadians: number,
  radius: number,
) => {
  const secondCircumferenceX = centreX + radius * Math.cos(startAngleRadians + sweepAngleRadians * 0.5);
  const secondCircumferenceY = centreY + radius * Math.sin(startAngleRadians + sweepAngleRadians * 0.5);

  return [secondCircumferenceX, secondCircumferenceY];
};

/**
 * Maps each sub-emotion to its primary emotion category
 * Used for grouping check-ins in reports and analytics
 */
export const emotionToPrimaryMap: Record<string, string> = {
  // Calm
  Serious: 'Calm',
  Bored: 'Calm',
  Quiet: 'Calm',
  Thankful: 'Calm',
  Peaceful: 'Calm',
  Content: 'Calm',
  Relaxed: 'Calm',
  // Happy
  Loved: 'Happy',
  'Excited/Energetic': 'Happy',
  Joyful: 'Happy',
  'Optimistic/Hopeful': 'Happy',
  Grateful: 'Happy',
  Amused: 'Happy',
  Passionate: 'Happy',
  // Confident
  'Brave/Courageous': 'Confident',
  Assertive: 'Confident',
  Powerful: 'Confident',
  'Respected/Valued': 'Confident',
  Intelligent: 'Confident',
  Proud: 'Confident',
  Important: 'Confident',
  // Surprised
  Startled: 'Surprised',
  Overwhelmed: 'Surprised',
  Shocked: 'Surprised',
  Confused: 'Surprised',
  Stunned: 'Surprised',
  Amazed: 'Surprised',
  Fascinated: 'Surprised',
  // Angry
  Hurt: 'Angry',
  Upset: 'Angry',
  Jealous: 'Angry',
  Frustrated: 'Angry',
  Annoyed: 'Angry',
  Offended: 'Angry',
  Irritated: 'Angry',
  // Scared
  Anxious: 'Scared',
  Intimidated: 'Scared',
  Embarrassed: 'Scared',
  Weak: 'Scared',
  'Distracted/Forgetful': 'Scared',
  Stressed: 'Scared',
  Nervous: 'Scared',
  // Sad
  Disappointed: 'Sad',
  Insecure: 'Sad',
  'Guilty/Ashamed': 'Sad',
  Depressed: 'Sad',
  'Lonely/Disconnected': 'Sad',
  Discouraged: 'Sad',
  Misunderstood: 'Sad',
};

/**
 * Helper function to get the primary emotion from a sub-emotion
 * @param emotion - The sub-emotion string
 * @returns The primary emotion category, or the original emotion if not found
 */
export const getPrimaryEmotion = (emotion: string): string => {
  return emotionToPrimaryMap[emotion] || emotion;
};
